import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SharedModule } from '@aiwyn/shared';
import {
  CRL_IN_REVIEW_ENABLED,
  CRL_TILE_FILTER_KEY,
  CrlCompletionState,
  CrlFilterKey,
  CrlFilterSelection,
  CrlProgressSummary,
  CrlTileCounts,
} from '../../../../models';

/**
 * Completion + granular metric tiles for the CRL list view (REQ-8).
 *
 * Left: overall completion tile — "[done] of [total] requests done", a progress
 * bar, and a state label (Not Started / Partially Complete / Complete) with
 * placeholder copy when nothing has started. Right: the CLICKABLE metric tiles
 * — Waiting for You (with an overdue callout), In Review, Needs Update. Clicking
 * a metric tile toggles its matching filter bucket (PP-3642): the component only
 * emits the `CrlFilterKey` via `tileToggled`; the container owns the selection
 * and applies it, so tiles compose through the SAME selection mechanism as the
 * Filters menu. The active state reflects the passed-in `selection`. The
 * completion tile is a readout, not a filter. All counts are pre-computed by the
 * selector; this component only renders them.
 *
 * The In Review tile renders only while {@link CRL_IN_REVIEW_ENABLED} surfaces
 * that concept; otherwise its count is always 0 and the row is the completion
 * tile plus two metric tiles.
 */
@Component({
  selector: 'cxp-crl-progress-tiles',
  templateUrl: './crl-progress-tiles.component.html',
  styleUrls: ['./crl-progress-tiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, MatProgressBarModule],
})
export class CrlProgressTilesComponent {
  readonly progress = input.required<CrlProgressSummary>();
  readonly tiles = input.required<CrlTileCounts>();
  /** Active filter buckets — drives each metric tile's selected state. */
  readonly selection = input<CrlFilterSelection>(new Set<CrlFilterKey>());

  /** Emits the metric tile's matching filter bucket for the container to toggle. */
  readonly tileToggled = output<CrlFilterKey>();

  protected readonly completionState = CrlCompletionState;
  /** The filter key each metric tile toggles (template binds these). */
  protected readonly filterKey = CRL_TILE_FILTER_KEY;
  /** Whether the In Review tile is surfaced at all (PP-3826). */
  protected readonly isInReviewEnabled = CRL_IN_REVIEW_ENABLED;

  protected readonly stateLabel = computed(() => {
    switch (this.progress().state) {
      case CrlCompletionState.COMPLETE:
        return 'Complete';
      case CrlCompletionState.PARTIALLY_COMPLETE:
        return 'Partially Complete';
      case CrlCompletionState.NOT_STARTED:
      default:
        return 'Not Started';
    }
  });

  protected readonly isNotStarted = computed(() => this.progress().state === CrlCompletionState.NOT_STARTED);

  isTileActive(key: CrlFilterKey): boolean {
    return this.selection().has(key);
  }

  onTileToggled(key: CrlFilterKey): void {
    this.tileToggled.emit(key);
  }
}
